// Campaigns API functions

import { apiRequest, API_BASE } from './api';
import { auth } from './firebase';
import type {
  Campaign,
  CampaignListResponse,
  CampaignCreateResponse,
  ContactListResponse,
  Template,
  CountryCode,
  StatusUpdateResponse,
} from '@/types/campaigns';

// Templates
export async function getTemplates(): Promise<Template[]> {
  return apiRequest<Template[]>('/campaigns/templates');
}

export async function downloadTemplateCSV(templateId: string): Promise<Blob> {
  const token = await auth.currentUser?.getIdToken();
  const response = await fetch(
    `${API_BASE}/campaigns/templates/${templateId}/csv`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!response.ok) {
    throw new Error('Failed to download template CSV');
  }
  return response.blob();
}

// Country codes
export async function getCountryCodes(): Promise<CountryCode[]> {
  return apiRequest<CountryCode[]>('/campaigns/country-codes');
}

// Campaigns CRUD
export async function getCampaigns(
  status?: string,
  includeArchived: boolean = false,
): Promise<Campaign[]> {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (includeArchived) params.append('include_archived', 'true');
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const response = await apiRequest<CampaignListResponse>(
    `/campaigns${queryString}`,
  );
  return response.campaigns;
}

export async function getCampaign(campaignId: string): Promise<Campaign> {
  return apiRequest<Campaign>(`/campaigns/${campaignId}`);
}

export async function createCampaign(
  templateId: string,
  csvFile: File,
  options: {
    name?: string;
    channel?: 'voice' | 'sms';
    countryCode?: string;
    startHour?: number;
    endHour?: number;
    timezone?: string;
    callReason?: string;
    enableSurvey?: boolean;
    enableSmsFollowUp?: boolean;
    smsTemplate?: string;
    allowedDays?: string[];
  } = {},
): Promise<CampaignCreateResponse> {
  const formData = new FormData();
  formData.append('template_id', templateId);
  formData.append('csv_file', csvFile);

  // Optional fields - only append if provided
  if (options.name) formData.append('name', options.name);
  if (options.channel) formData.append('channel', options.channel);
  if (options.countryCode) formData.append('country_code', options.countryCode);
  if (options.startHour !== undefined)
    formData.append('start_hour', options.startHour.toString());
  if (options.endHour !== undefined)
    formData.append('end_hour', options.endHour.toString());
  if (options.timezone) formData.append('timezone', options.timezone);
  if (options.callReason) formData.append('call_reason', options.callReason);
  if (options.enableSurvey !== undefined)
    formData.append('enable_survey', options.enableSurvey.toString());
  if (options.enableSmsFollowUp !== undefined)
    formData.append(
      'enable_sms_follow_up',
      options.enableSmsFollowUp.toString(),
    );
  if (options.smsTemplate) formData.append('sms_template', options.smsTemplate);
  if (options.allowedDays)
    formData.append('allowed_days', JSON.stringify(options.allowedDays));

  return apiRequest<CampaignCreateResponse>('/campaigns', {
    method: 'POST',
    body: formData,
  });
}

// Campaign controls
export async function startCampaign(
  campaignId: string,
): Promise<StatusUpdateResponse> {
  return apiRequest<StatusUpdateResponse>(`/campaigns/${campaignId}/start`, {
    method: 'POST',
  });
}

export async function pauseCampaign(
  campaignId: string,
): Promise<StatusUpdateResponse> {
  return apiRequest<StatusUpdateResponse>(`/campaigns/${campaignId}/pause`, {
    method: 'POST',
  });
}

export async function archiveCampaign(
  campaignId: string,
): Promise<StatusUpdateResponse> {
  return apiRequest<StatusUpdateResponse>(`/campaigns/${campaignId}/archive`, {
    method: 'POST',
  });
}

// Contacts
export async function getCampaignContacts(
  campaignId: string,
  status?: string,
  limit: number = 100,
  offset: number = 0,
): Promise<ContactListResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });
  if (status) {
    params.append('status', status);
  }
  return apiRequest<ContactListResponse>(
    `/campaigns/${campaignId}/contacts?${params.toString()}`,
  );
}

// Template settings
export interface TemplateSettings {
  default_call_reason?: string;
  default_sms_template?: string;
  default_start_hour?: number;
  default_end_hour?: number;
  default_channel?: 'voice' | 'sms';
  default_enable_survey?: boolean;
  default_enable_sms_follow_up?: boolean;
}

export async function saveTemplateSettings(
  templateId: string,
  settings: TemplateSettings,
): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>(
    `/campaigns/templates/${templateId}`,
    {
      method: 'PUT',
      body: settings as Record<string, unknown>,
    },
  );
}

export async function resetTemplateSettings(
  templateId: string,
): Promise<{ success: boolean; message: string }> {
  return apiRequest<{ success: boolean; message: string }>(
    `/campaigns/templates/${templateId}`,
    { method: 'DELETE' },
  );
}

// Note: Agent selection is now handled by backend automatically
// No need to fetch agents for campaign creation
